import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSessionManager } from '@/hooks/useSessionManager';
import { useSessionTokenHash } from '@/hooks/useSessionTokenHash';
import { useUpsertSession } from '@/hooks/useUserSessions';
import { hashSessionToken } from '@/lib/sessionToken';
import { SessionTimeoutModal } from './SessionTimeoutModal';
import { useAuth } from '@/lib/auth';
import { useActiveClinic } from '@/lib/activeClinic';
import { getDeviceInfo } from '@/lib/deviceFingerprint';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

/**
 * Core session management component that should be mounted at the app root.
 * Handles:
 * - Session creation on login
 * - Activity tracking
 * - Timeout warnings
 * - Automatic logout
 */
export function SessionManager() {
  const { t } = useTranslation();
  const location = useLocation();
  const isPortalRoute = location.pathname.startsWith('/portal');
  const { session, signOut, user } = useAuth();
  const { activeClinicId } = useActiveClinic();
  const { mutateAsync: upsertSession } = useUpsertSession();
  const sessionTokenHash = useSessionTokenHash(session?.access_token);
  const lastRegisteredTokenRef = useRef<string | null>(null);

  // Session manager with timeout tracking
  const {
    showWarning,
    remainingSeconds,
    extendSession,
    settings,
  } = useSessionManager({
    clinicId: activeClinicId,
    enabled: !isPortalRoute,
    onTimeout: () => {
      toast.error(t('session.timeout.expired', 'Your session has expired due to inactivity'));
    },
  });

  // Create/update session on login (and when the access token rotates)
  useEffect(() => {
    if (isPortalRoute || !session?.access_token || !session?.refresh_token || !sessionTokenHash) {
      return;
    }

    if (lastRegisteredTokenRef.current === sessionTokenHash) {
      return;
    }

    let cancelled = false;

    const registerSession = async () => {
      try {
        const refreshTokenHash = await hashSessionToken(session.refresh_token);

        const deviceInfo = getDeviceInfo();

        const expiresAt = new Date(
          Date.now() + (settings?.absolute_timeout_hours ?? 12) * 60 * 60 * 1000,
        ).toISOString();

        await upsertSession({
          sessionToken: sessionTokenHash,
          refreshTokenHash,
          deviceInfo: deviceInfo as unknown as Record<string, unknown>,
          expiresAt,
        });

        if (!cancelled) {
          lastRegisteredTokenRef.current = sessionTokenHash;
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        console.error('[SessionManager] Failed to register session:', msg, error);
      }
    };

    void registerSession();

    return () => {
      cancelled = true;
    };
  }, [
    isPortalRoute,
    session?.access_token,
    session?.refresh_token,
    sessionTokenHash,
    settings?.absolute_timeout_hours,
    upsertSession,
  ]);

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error(t('session.logout.error', 'Failed to log out'));
    }
  };

  if (!user || !session || isPortalRoute) return null;

  return (
    <SessionTimeoutModal
      isOpen={showWarning}
      remainingSeconds={remainingSeconds}
      onExtend={extendSession}
      onLogout={handleLogout}
    />
  );
}
